import { Vec2 } from '../utils/math';

/**
 * 2D camera with pan and zoom for viewport transformation.
 * World coordinates are the simulation space; screen coordinates are canvas pixels.
 */
export class Camera {
  position: Vec2 = new Vec2(0, 0); // world position of the viewport center
  zoom: number = 1;

  private minZoom = 0.1;
  private maxZoom = 10;

  /** Convert world coordinates to screen coordinates */
  worldToScreen(world: Vec2, canvasWidth: number, canvasHeight: number): Vec2 {
    return new Vec2(
      (world.x - this.position.x) * this.zoom + canvasWidth / 2,
      (world.y - this.position.y) * this.zoom + canvasHeight / 2,
    );
  }

  /** Convert screen coordinates to world coordinates */
  screenToWorld(screen: Vec2, canvasWidth: number, canvasHeight: number): Vec2 {
    return new Vec2(
      (screen.x - canvasWidth / 2) / this.zoom + this.position.x,
      (screen.y - canvasHeight / 2) / this.zoom + this.position.y,
    );
  }

  /** Pan the camera by a screen-space delta */
  pan(screenDx: number, screenDy: number): void {
    this.position = new Vec2(
      this.position.x - screenDx / this.zoom,
      this.position.y - screenDy / this.zoom,
    );
  }

  /** Zoom toward a screen point (keeps that point fixed) */
  zoomAt(screenX: number, screenY: number, factor: number, canvasWidth: number, canvasHeight: number): void {
    const worldBefore = this.screenToWorld(new Vec2(screenX, screenY), canvasWidth, canvasHeight);
    this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * factor));
    const worldAfter = this.screenToWorld(new Vec2(screenX, screenY), canvasWidth, canvasHeight);
    // Adjust position so the point under the cursor stays fixed
    this.position = new Vec2(
      this.position.x + (worldBefore.x - worldAfter.x),
      this.position.y + (worldBefore.y - worldAfter.y),
    );
  }

  /** Apply camera transform to a canvas 2D context */
  applyTransform(ctx: CanvasRenderingContext2D, dpr: number, canvasWidth: number, canvasHeight: number): void {
    ctx.setTransform(
      this.zoom * dpr, 0,
      0, this.zoom * dpr,
      (-this.position.x * this.zoom + canvasWidth / 2) * dpr,
      (-this.position.y * this.zoom + canvasHeight / 2) * dpr,
    );
  }

  /** Reset to default view */
  reset(): void {
    this.position = new Vec2(0, 0);
    this.zoom = 1;
  }
}
